import Texture2D from 'nanogl/texture-2d'

import { AtlasData, Bounds } from '@/webgl/engine/FontAtlas.types'

export type TextStyle = {
  fontSize?: number
  lineHeight?: number
  alignMiddle?: boolean
  boxWidth?: number
  center?: boolean
  color?: number
  outline?: number
  outlineColor?: number
}

const defaultStyle: TextStyle = {
  fontSize: 1.0,
  lineHeight: 1.0,
  boxWidth: 10000,
  center: false,
}

export type TextRenderInfo = {
  offset: number
  count: number
  width: number
  height: number
}

export default class FontAtlas {
  constructor(
    readonly texture: Texture2D,
    readonly data: AtlasData
  ) {}

  atlasToUV(atlasBounds: Bounds, width: number, height: number) {
    return {
      u1: atlasBounds.left / width,
      v1: 1.0 - atlasBounds.bottom / height,
      u2: atlasBounds.right / width,
      v2: 1.0 - atlasBounds.top / height,
    }
  }

  createText(text: string, style: TextStyle = {}) {
    const _style = { ...defaultStyle }
    Object.assign(_style, style)

    const positions = []
    const uvs = []
    const indices = []

    const FONT_SIZE = _style.fontSize
    let OFFSET_Y = _style.lineHeight - 1.0
    if (_style.alignMiddle) {
      OFFSET_Y = -FONT_SIZE / 2
    }

    let x = 0

    for (const char of text) {
      const glyph = this.data.glyphs.find((g) => g.unicode === char.charCodeAt(0))
      if (!glyph) continue
      if (!glyph.planeBounds) {
        x += glyph.advance * FONT_SIZE
        continue
      }

      const { left, bottom, right, top } = glyph.planeBounds
      const { u1, v1, u2, v2 } = this.atlasToUV(glyph.atlasBounds, this.data.atlas.width, this.data.atlas.height)

      // Ajouter les positions des sommets (2 triangles par glyphe)
      positions.push(
        x + left * FONT_SIZE,
        bottom * FONT_SIZE + OFFSET_Y, // bas gauche
        x + right * FONT_SIZE,
        bottom * FONT_SIZE + OFFSET_Y, // bas droite
        x + left * FONT_SIZE,
        top * FONT_SIZE + OFFSET_Y, // haut gauche
        x + right * FONT_SIZE,
        bottom * FONT_SIZE + OFFSET_Y, // bas droite
        x + right * FONT_SIZE,
        top * FONT_SIZE + OFFSET_Y, // haut droite
        x + left * FONT_SIZE,
        top * FONT_SIZE + OFFSET_Y // haut gauche
      )

      indices.push(
        indices.length,
        indices.length + 1,
        indices.length + 2,
        indices.length + 3,
        indices.length + 4,
        indices.length + 5
      )

      // Ajouter les coordonnées UV correspondantes
      uvs.push(u1, v1, u2, v1, u1, v2, u2, v1, u2, v2, u1, v2)

      x += glyph.advance * FONT_SIZE
    }

    if (_style.center) {
      for (let i = 0; i < positions.length; i += 2) {
        positions[i] -= x / 2
      }
    }

    return {
      positions: new Float32Array(positions),
      uvs: new Float32Array(uvs),
      indices: new Uint16Array(indices),
    }
  }
}
